import {Cart, CartFactory, ICartPosition} from "@connox/typescript-library";
import {CurrencyCode} from "@connox/typescript-library/types/cart/CurrencyCode";
import * as React from "react";
import {useEffect, useState} from "react";
import {UrlSearchParamsHelper} from "../../../helpers/urlSearchParamsHelper";
import {useCustomerContext} from "../../customer/CustomerContextProvider/CustomerContextProvider";
import {getShopId} from "../../lib/getShopId";
import {isAmazonLoggedIn} from "../../lib/payments/AmazonPayLibrary";
import window from "../../lib/WindowExtended";
import {IProductTrackingData, Tracking} from "../../../tracking/tracking";
import {ICartPromotion} from "@connox/typescript-library/types/cart/CartPromotion";
import {sentryAddBreadcrumb} from "../../../helpers/sentryHelper";

interface ICurrencySelector extends HTMLFormElement {
  CurrencyCode: HTMLSelectElement;
}

export interface IPartials {
  partialAddress: {
    email: string;
    firstName: string;
    lastName: string;
    gender: "M" | "F";
  };
  isGuest: boolean;
  isCorporateCustomer: boolean;
}

export interface IViewState {
  partials?: IPartials;
  isRegistering: boolean;
}

interface ICartContext {
  cart: Cart;

  isIntoCheckout: boolean;
  setIsIntoCheckout: (isIntoCheckout: boolean) => void;

  isCartVisible: boolean;
  setIsCartVisible: (isCartVisible: boolean) => void;

  itemsAdded: number[];
  setItemsAdded: (itemIds: number[]) => void;

  setViewState: (viewState?: IViewState) => void;
  viewState: IViewState;

  setVoucherAdded: (voucherAdded: string) => void;
  voucherAdded: string;
}

interface IRequestCart {
  cart: Cart;
  done: () => void;
}

export interface ICartContextNoCart {
  requestCart: () => Promise<IRequestCart>;

  isCartVisible: boolean;
  setIsCartVisible: (isCartVisible: boolean) => void;

  setItemsAdded: (itemIds: number[]) => void;
  itemsAdded: number[];

  setVoucherAdded: (voucherAdded: string) => void;
  voucherAdded: string;
}

type IUnsafeCartContext = ICartContext | ICartContextNoCart | null;

const unsafeCartContext = React.createContext<IUnsafeCartContext>(null);

export const isCart = (cart: IUnsafeCartContext): cart is ICartContext => {
  return (cart as ICartContext).cart !== undefined;
};

export const useCartContext = (): ICartContext => {
  const cartContext = React.useContext(unsafeCartContext);
  if (!isCart(cartContext)) {
    throw new Error("useCartContext must be used in a component within a CartContextProvider.");
  }

  return cartContext;
};

export const useUnsafeCartContext = (): IUnsafeCartContext => {
  return React.useContext(unsafeCartContext);
};

const CartContextProvider: React.FC = (props) => {
  const setItemsAdded = (itemsAdded: number[]) => {
    setState((prevState) => {
      if (!prevState) {
        return null;
      }

      return {...prevState, itemsAdded};
    });
  };
  /**
   * sets added voucher, to open the popup
   * @param voucherAdded voucher code string that was added to trigger voucher popup
   */
  const setVoucherAdded = (voucherAdded: string) => {
    setState((prevState) => {
      if (!prevState) {
        return null;
      }

      return {...prevState, voucherAdded};
    });
  };

  /**
   * get the set currency, if respective form is shown
   * most probably the case in sales channel 100005 / connox.com
   */
  const getCurrencyFromModal = (): CurrencyCode | undefined => {
    const selectCurrencyForm = document.querySelector<ICurrencySelector>("#SelectCurrencyModal > form");
    if (!selectCurrencyForm) {
      return undefined;
    }
    const preselectedCurrency = selectCurrencyForm.CurrencyCode.selectedOptions[0].value;
    if (!preselectedCurrency) {
      return undefined;
    }

    return preselectedCurrency as CurrencyCode;
  };

  /**
   * get promotion params if existing
   */
  const getPromotionData = (): ICartPromotion | undefined => {
    if (!window.c_PromotionId) {
      return undefined;
    }

    return {
      id: Number(window.c_PromotionId),
      reference: window.c_PromotionContentReference || undefined,
    };
  };

  /**
   * update currency code and promotion id if necessary
   * @param cart
   */
  const updateCartSettings = async (cart: Cart) => {
    const promotion = getPromotionData();
    const preselectedCurrency = getCurrencyFromModal();
    const {isCorporateCustomer} = customer;

    if (
      (preselectedCurrency && preselectedCurrency !== cart.currencyCode) ||
      (promotion && promotion.id !== cart.promotion?.id) ||
      cart.isCorporateCustomer !== isCorporateCustomer
    ) {
      await cart.put({currencyCode: preselectedCurrency, promotion, isCorporateCustomer});
    }
  };

  const cartContextNoCart: ICartContextNoCart = {
    requestCart: async (): Promise<IRequestCart> => {
      const activeCountry = window.c_ActiveCountry;
      const activeCurrency = getCurrencyFromModal() ?? (window.c_ActiveCurrency as CurrencyCode);
      const promotionData = getPromotionData();

      return await CartFactory.generateCart(getShopId(), customer.visitorId, {
        countryCode: activeCountry,
        currencyCode: activeCurrency,
        promotion: promotionData,
        isCorporateCustomer: customer.isCorporateCustomer,
      }).then((cart) => {
        // this must be called by the callback once the fallback is done
        const done = () => {
          updateCart(cart);
        };

        return {cart, done};
      });
    },
    setItemsAdded,
    itemsAdded: [],

    setVoucherAdded,
    voucherAdded: "",

    setIsCartVisible: (isCartVisible: boolean) =>
      setState((prevState) => {
        if (!prevState) {
          return null;
        }

        return {...prevState, isCartVisible};
      }),
    isCartVisible: false,
  };

  const [state, setState] = useState<IUnsafeCartContext>(cartContextNoCart);
  const {customer} = useCustomerContext();

  // We need this ref to use it in clean up useEffect callback
  const cartRef = React.useRef<Cart | null>(null);

  // region check for navigation in cart or checkout
  const params = new URLSearchParams(window.location.search);
  // when this is present we assume a cart navigation which leads to an visible cart / checkout by default
  const goToStepGetParameter = params.get("gotoStep");
  const isNavigationStep = !!goToStepGetParameter;
  // endregion

  const setPartialCartContextState = (partialState: Partial<ICartContext>) => {
    setState((previousState) => {
      if (!isCart(previousState)) {
        return null;
      }

      return {...previousState, ...partialState};
    });
  };

  const trackCartItems = (positions: ICartPosition[]) => {
    const positionsToTrack = positions.reduce<IProductTrackingData[]>((akkumulator, current) => {
      if (current.type === "Item" && !!current.item && !!current.reference?.itemId) {
        akkumulator.push({
          name: current.text,
          id: current.item.itemId,
          brand: current.item.manufacturer,
          price: current.price.amount,
          quantity: current.quantity,
        });
      }

      return akkumulator;
    }, []);

    Tracking.setCartItems(positionsToTrack);
  };

  const refreshCart = () => {
    if (cartRef.current) {
      trackCartItems(cartRef.current.positions);
    }
    setPartialCartContextState({});
  };

  useEffect(() => {
    if (UrlSearchParamsHelper.containsParams()) {
      void cartContextNoCart.requestCart().then(async ({cart, done}) => {
        const itemsAdded: number[] = [];
        let voucherAdded = "";
        UrlSearchParamsHelper.itemIds.map(async (item) => {
          // this assumes that the following promise resolves without error - this is not defensive coding, it's mvp
          itemsAdded.push(item.itemId);
          await cart.addItem(item.itemId, item.quantity);
        });

        if (UrlSearchParamsHelper.voucherCode) {
          voucherAdded = UrlSearchParamsHelper.voucherCode;
          await cart.addVoucher(UrlSearchParamsHelper.voucherCode);
        }

        done();

        if (state && (itemsAdded.length > 0 || voucherAdded !== "")) {
          setState((prevState) => {
            if (!prevState) {
              return null;
            }

            return {...prevState, itemsAdded, voucherAdded};
          });
        }
      });
    } else {
      void CartFactory.getCartForVisitorId(getShopId(), customer.visitorId).then(async (existingCart) => {
        if (!existingCart) {
          return;
        }
        await updateCartSettings(existingCart);
        updateCart(existingCart);
      });
    }

    // clean up of event listeners to prevent setting state in unmounted components
    return () => {
      if (!cartRef.current) {
        return;
      }
      cartRef.current.emitter.off(refreshCart);
    };
  }, []);

  useEffect(() => {
    if (state?.isCartVisible) {
      Tracking.sendEvent("trackCartPopupOpened");
    }
  }, [state?.isCartVisible]);

  useEffect(() => {
    if (state && state.itemsAdded.length && isCart(state)) {
      const addedPositions = state.cart.positions
        .filter<ICartPosition>(
          (pos): pos is ICartPosition =>
            pos.type === "Item" && !!pos.reference?.itemId && !!pos.item && state.itemsAdded.includes(pos.item.itemId),
        )
        .map<IProductTrackingData>((pos) => ({
          brand: pos.item?.manufacturer ?? null,
          id: pos.reference?.itemId ?? 0,
          name: pos.text,
          price: pos.price.amount,
          quantity: pos.quantity,
        }));

      if (addedPositions.length) {
        Tracking.sendAddToCart(addedPositions, state.cart.currencyCode);
      }
    }
  }, [state?.itemsAdded.length]);

  // this is only called when the cart is being initialized
  const updateCart = (cart: Cart): void => {
    cart.emitter.on(refreshCart);
    cartRef.current = cart;
    trackCartItems(cart.positions);

    setState((prevState) => {
      return {
        cart,

        // the following is not really my doing
        isIntoCheckout: (isNavigationStep && isAmazonLoggedIn()) || goToStepGetParameter === "magicLink",
        setIsIntoCheckout: (isIntoCheckout: boolean) => setPartialCartContextState({isIntoCheckout}),

        isCartVisible: prevState ? isNavigationStep || prevState.isCartVisible : false,
        setIsCartVisible: (isCartVisible: boolean) => {
          sentryAddBreadcrumb(
            isCartVisible ? "opening cart" : "closing cart",
            null,
            "setIsCartVisible",
            undefined,
            customer,
          );
          setPartialCartContextState({isCartVisible});
        },

        itemsAdded: prevState ? prevState.itemsAdded : [],
        setItemsAdded,

        setVoucherAdded,
        voucherAdded: prevState ? prevState.voucherAdded : "",

        setViewState: (viewState: IViewState | undefined) => setPartialCartContextState({viewState}),
        viewState: {isRegistering: false},
      };
    });
  };

  const {Provider} = unsafeCartContext;

  return <Provider value={state}>{props.children}</Provider>;
};

export default CartContextProvider;
